/**
 * RendererBoundary — the "a malformed block must never crash a turn" rule (#930).
 *
 * Every rich renderer (chart, mermaid, callout) is progressive enhancement on
 * top of a plain fenced code block. If one of them throws — bad spec, renderer
 * bug, upstream library regression — the turn keeps rendering and the block
 * degrades to the fallback (the plain code block), instead of white-screening
 * the whole panel the way an uncaught render error would.
 *
 * `resetKey` matters during streaming: a block's content grows chunk by chunk,
 * and an early malformed prefix must not poison the boundary for the completed
 * block. When the key changes, the error state clears and the renderer gets
 * another attempt.
 */
import { Component, type ReactNode } from 'react';

interface RendererBoundaryProps {
  fallback: ReactNode;
  resetKey: string;
  children: ReactNode;
}

interface RendererBoundaryState {
  hasError: boolean;
  erroredKey: string | null;
}

export class RendererBoundary extends Component<RendererBoundaryProps, RendererBoundaryState> {
  state: RendererBoundaryState = { hasError: false, erroredKey: null };

  static getDerivedStateFromError(): Partial<RendererBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(): void {
    // Remember which content errored so a NEW content string retries.
    this.setState({ erroredKey: this.props.resetKey });
  }

  componentDidUpdate(prev: RendererBoundaryProps): void {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, erroredKey: null });
    }
  }

  render(): ReactNode {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}
